import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useUserStore } from '@/store/use-user-store';
import { Icon } from '@iconify/react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Tooltip,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface ExportFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filters: ExportFilters) => void;
  isLoading: boolean;
  title: string;
  type: 'practices' | 'user-access';
}

interface ExportFilters {
  start_date?: string;
  end_date?: string;
  chunk_size?: number;
  batch_size?: number;
  include_discussion?: boolean;
  identify_user?: boolean;
}

// Note: we will make this dynamic in future
const ALLOWED_EMAILS_DOWNLOAD_DISCUSSION = [
  'hitendra@mentora.institute',
  'amy@lift.zenafide.com',
  'srikanthm@zenafide.com',
  'judy@mentora.institute',
];

const MAX_DATE_RANGE_DAYS = 360; // Maximum allowed date range in days

// Helper function to format date to YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get default dates (last 30 days)
const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);

  return {
    start_date: formatDate(start),
    end_date: formatDate(end),
  };
};

export default function ExportFiltersModal({
  isOpen,
  onClose,
  onExport,
  isLoading,
  title,
}: ExportFiltersModalProps) {
  const defaultDates = getDefaultDates();
  const [filters, setFilters] = useState<ExportFilters>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
    chunk_size: 500,
    identify_user: false,
    // include_discussion: false,
  });
  const { tenantId } = useActiveTenant();
  const user = useUserStore((state) => state.user);

  // Reset dates when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultDates = getDefaultDates();
      setFilters((prev) => ({
        ...prev,
        start_date: defaultDates.start_date,
        end_date: defaultDates.end_date,
        tenant_id: tenantId,
        identify_user: false,
      }));
    }
  }, [isOpen]);

  // Extra check for include discussion for only hitendra
  // we have created new permission for this but can be misused through django admin
  const includeDiscussion = ALLOWED_EMAILS_DOWNLOAD_DISCUSSION.includes(
    user?.email || ''
  );

  console.log('includeDiscussion', includeDiscussion, user?.email);

  const validateDates = (): boolean => {
    if (!filters.start_date || !filters.end_date) {
      toast.error('Please select both start and end dates');
      return false;
    }

    const start = new Date(filters.start_date);
    const end = new Date(filters.end_date);

    if (end < start) {
      toast.error('End date cannot be before start date');
      return false;
    }

    // Check if date range exceeds maximum allowed days
    const daysDiff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff > MAX_DATE_RANGE_DAYS) {
      toast.error(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }

    return true;
  };

  const handleExport = () => {
    if (!validateDates()) return;

    // Remove undefined or empty string values
    const cleanedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        const isEnabledIdentifyUser = key === 'identify_user' && value === true;
        if (
          value !== undefined &&
          value !== '' &&
          (key !== 'identify_user' || isEnabledIdentifyUser)
        ) {
          acc[key as keyof ExportFilters] = value;
        }

        return acc;
      },
      {} as ExportFilters
    );

    onExport(cleanedFilters);
  };

  const handleDateChange = (
    field: 'start_date' | 'end_date',
    value: string
  ) => {
    // Ensure the date is in YYYY-MM-DD format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value)) return;

    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetDates = () => {
    const defaultDates = getDefaultDates();
    setFilters((prev) => ({
      ...prev,
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
      include_discussion: includeDiscussion,
    }));
    toast.success('Dates reset to last 30 days');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      classNames={{
        base: 'max-h-[90vh] overflow-y-auto',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Export {title}
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Date Range</span>
                <Tooltip content="Reset to last 30 days">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={handleResetDates}
                    disabled={isLoading}
                  >
                    <Icon icon="mdi:refresh" width="20" />
                  </Button>
                </Tooltip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Start Date"
                  placeholder="YYYY-MM-DD"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleDateChange('start_date', e.target.value)
                  }
                  pattern="\d{4}-\d{2}-\d{2}"
                  max={filters.end_date}
                />
                <Input
                  type="date"
                  label="End Date"
                  placeholder="YYYY-MM-DD"
                  value={filters.end_date}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
                  pattern="\d{4}-\d{2}-\d{2}"
                  min={filters.start_date}
                />
              </div>
            </div>

            {/* <Select
              label="Chunk Size"
              placeholder="Select chunk size"
              selectedKeys={[filters.chunk_size?.toString() || '500']}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  chunk_size: parseInt(e.target.value, 10),
                }))
              }
            >
              {CHUNK_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select> */}

            {includeDiscussion && (
              <div className="flex items-center gap-2">
                <Switch
                  isSelected={filters.include_discussion}
                  onValueChange={(checked) =>
                    setFilters((prev) => ({
                      ...prev,
                      include_discussion: checked,
                    }))
                  }
                />
                <span>Include Discussion</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                isSelected={filters.identify_user}
                onValueChange={(checked) =>
                  setFilters((prev) => ({
                    ...prev,
                    identify_user: checked,
                  }))
                }
              />
              <span>Identify Users</span>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleExport} isLoading={isLoading}>
            Export
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
